import { track } from "analytics/analytics";
import { LegacyClusterInfo } from "in_queue/types/clusterType";

const IN_QUEUE_PREFIX = "in_queue_";
const PROJECT_ID_PROPERTY = "project_id";
const SCENARIO_ID_PROPERTY = "scenario_id";
const REGION_PROPERTY = "region";
const STUDY_GROUP_PROPERTY = "study_group";
const STUDY_CYCLE_PROPERTY = "study_cycle";
const STUDY_PHASE_PROPERTY = "study_phase";
const SCENARIO_STAGE_PROPERTY = "scenario_stage";

type CLUSTER_PROPERTIES = {
    region: string;
    studyGroup: string;
    studyCycle: string;
    studyPhase: string;
};

/**
 * Standard cluster context attached to in-queue events so they can be
 * segmented by region / study group / cycle / phase in Amplitude.
 */
const clusterProperties = (
    cluster: LegacyClusterInfo
): Record<string, string> => ({
    [REGION_PROPERTY]: cluster.region,
    [STUDY_GROUP_PROPERTY]: cluster.studyGroup,
    [STUDY_CYCLE_PROPERTY]: cluster.studyCycle,
    [STUDY_PHASE_PROPERTY]: cluster.studyPhase
});

const FROM_PAGE_PROPERTY = "from_page";

// Records where the user navigated to Home from (e.g. the breadcrumb on a
// project/scenario page). Consumed once by the next open_home_page event so we
// avoid a separate click event that double-fires alongside the page view.
let pendingHomeFromPage: string | undefined;
export const setHomeNavigationSource = (fromPage: string): void => {
    pendingHomeFromPage = fromPage;
};

const OPEN_HOME_PAGE_NAME = `${IN_QUEUE_PREFIX}open_home_page`;
export const segmentTrackOpenHomePage = (): void => {
    const fromPage = pendingHomeFromPage;
    pendingHomeFromPage = undefined;
    track(
        OPEN_HOME_PAGE_NAME,
        fromPage !== undefined ? { [FROM_PAGE_PROPERTY]: fromPage } : {}
    );
};

const OPEN_PROJECT_PAGE_NAME = `${IN_QUEUE_PREFIX}open_project_page`;
export const segmentTrackOpenProjectPage = ({
    projectId,
    cluster
}: {
    projectId: string;
    cluster: LegacyClusterInfo;
}): void => {
    track(OPEN_PROJECT_PAGE_NAME, {
        [PROJECT_ID_PROPERTY]: projectId,
        ...clusterProperties(cluster)
    });
};

const OPEN_SCENARIO_PAGE_NAME = `${IN_QUEUE_PREFIX}open_scenario_page`;
export const segmentTrackOpenScenarioPage = ({
    scenarioId,
    projectId,
    cluster
}: {
    scenarioId: string;
    projectId: string;
    cluster: LegacyClusterInfo;
}): void => {
    track(OPEN_SCENARIO_PAGE_NAME, {
        [SCENARIO_ID_PROPERTY]: scenarioId,
        [PROJECT_ID_PROPERTY]: projectId,
        ...clusterProperties(cluster)
    });
};

const CREATE_SCENARIO_NAME = `${IN_QUEUE_PREFIX}create_scenario`;

const SCENARIO_TITLE_PROPERTY = "scenario_title";
const NUM_PROJECT_SIZE_ASSUMPTIONS_PROPERTY = "num_project_size_assumptions";
export const segmentTrackCreateScenario = (props: {
    region: string;
    studyGroup: string;
    studyCycle: string;
    studyPhase: string;
    scenarioStage: string;
    scenarioTitle: string;
    numProjectSizeAssumptions: number;
}): void => {
    track(CREATE_SCENARIO_NAME, {
        [REGION_PROPERTY]: props.region,
        [STUDY_GROUP_PROPERTY]: props.studyGroup,
        [STUDY_CYCLE_PROPERTY]: props.studyCycle,
        [STUDY_PHASE_PROPERTY]: props.studyPhase,
        [SCENARIO_STAGE_PROPERTY]: props.scenarioStage,
        [SCENARIO_TITLE_PROPERTY]: props.scenarioTitle,
        [NUM_PROJECT_SIZE_ASSUMPTIONS_PROPERTY]: props.numProjectSizeAssumptions
    });
};

const EDIT_SCENARIO_NAME = `${IN_QUEUE_PREFIX}edit_scenario`;
export const segmentTrackEditScenario = (props: {
    scenarioId: string;
    scenarioTitle: string;
    numProjectSizeAssumptions: number;
}): void => {
    track(EDIT_SCENARIO_NAME, {
        [SCENARIO_ID_PROPERTY]: props.scenarioId,
        [SCENARIO_TITLE_PROPERTY]: props.scenarioTitle,
        [NUM_PROJECT_SIZE_ASSUMPTIONS_PROPERTY]: props.numProjectSizeAssumptions
    });
};

const EDIT_SCENARIO_TITLE_NAME = `${IN_QUEUE_PREFIX}edit_scenario_title`;
export const segmentTrackEditScenarioTitle = (props: {
    scenarioId: string;
    scenarioTitle: string;
}): void => {
    track(EDIT_SCENARIO_TITLE_NAME, {
        [SCENARIO_ID_PROPERTY]: props.scenarioId,
        [SCENARIO_TITLE_PROPERTY]: props.scenarioTitle
    });
};

const ARCHIVE_SCENARIO_NAME = `${IN_QUEUE_PREFIX}archive_scenario`;
const SCENARIO_STATUS_PROPERTY = "status";
const SCENARIO_ORIGIN_PROPERTY = "scenario_origin";
export const segmentTrackArchiveScenario = ({
    scenarioId,
    action,
    scenarioOrigin
}: {
    scenarioId: string;
    action: "archive" | "unarchive";
    scenarioOrigin: string | undefined;
}): void => {
    track(ARCHIVE_SCENARIO_NAME, {
        [SCENARIO_ID_PROPERTY]: scenarioId,
        [SCENARIO_STATUS_PROPERTY]:
            action === "archive" ? "ARCHIVED" : "UNARCHIVED",
        ...(scenarioOrigin !== undefined
            ? { [SCENARIO_ORIGIN_PROPERTY]: scenarioOrigin }
            : {})
    });
};

const BULK_ARCHIVE_SCENARIOS_NAME = `${IN_QUEUE_PREFIX}bulk_archive_scenarios`;
const NUM_PUBLISHED_SCENARIOS_PROPERTY = "num_published_scenarios";

export const segmentTrackBulkArchiveScenarios = ({
    projectId,
    numScenarios,
    numPublishedScenarios
}: {
    projectId: string;
    numScenarios: number;
    numPublishedScenarios: number;
}): void => {
    track(BULK_ARCHIVE_SCENARIOS_NAME, {
        [PROJECT_ID_PROPERTY]: projectId,
        [NUM_SCENARIOS_PROPERTY]: numScenarios,
        [NUM_PUBLISHED_SCENARIOS_PROPERTY]: numPublishedScenarios
    });
};

const BULK_RERUN_SCENARIOS_NAME = `${IN_QUEUE_PREFIX}bulk_rerun_scenarios`;

export const segmentTrackBulkRerunScenarios = ({
    projectId,
    numScenarios
}: {
    projectId: string;
    numScenarios: number;
}): void => {
    track(BULK_RERUN_SCENARIOS_NAME, {
        [PROJECT_ID_PROPERTY]: projectId,
        [NUM_SCENARIOS_PROPERTY]: numScenarios
    });
};

const TOGGLE_COMPARE_SCENARIOS_MODE_NAME = `${IN_QUEUE_PREFIX}toggle_compare_scenarios_mode`;
const COMPARE_MODE_ENABLED_PROPERTY = "enabled";
export const segmentTrackToggleCompareScenariosMode = ({
    projectId,
    enabled
}: {
    projectId: string;
    enabled: boolean;
}): void => {
    track(TOGGLE_COMPARE_SCENARIOS_MODE_NAME, {
        [PROJECT_ID_PROPERTY]: projectId,
        [COMPARE_MODE_ENABLED_PROPERTY]: enabled
    });
};

const COMPARE_SELECTED_SCENARIOS_NAME = `${IN_QUEUE_PREFIX}compare_selected_scenarios`;
const FIRST_SCENARIO_ID_PROPERTY = "first_scenario_id";
const SECOND_SCENARIO_ID_PROPERTY = "second_scenario_id";
type CompareScenariosProperties = {
    projectId: string;
    firstScenarioId: string;
    secondScenarioId: string;
};
const compareScenariosTrackProps = (
    props: CompareScenariosProperties
): Record<string, string> => ({
    [PROJECT_ID_PROPERTY]: props.projectId,
    [FIRST_SCENARIO_ID_PROPERTY]: props.firstScenarioId,
    [SECOND_SCENARIO_ID_PROPERTY]: props.secondScenarioId
});
export const segmentTrackCompareSelectedScenarios = (
    props: CompareScenariosProperties
): void => {
    track(COMPARE_SELECTED_SCENARIOS_NAME, compareScenariosTrackProps(props));
};

const COMPARE_SCENARIOS_SWAP_NAME = `${IN_QUEUE_PREFIX}compare_scenarios_swap`;
export const segmentTrackCompareScenariosSwap = (
    props: CompareScenariosProperties
): void => {
    track(COMPARE_SCENARIOS_SWAP_NAME, compareScenariosTrackProps(props));
};

const COMPARE_SCENARIOS_OPEN_FILTER_NAME = `${IN_QUEUE_PREFIX}compare_scenarios_open_filter`;
export const segmentTrackCompareScenariosOpenFilter = (): void => {
    track(COMPARE_SCENARIOS_OPEN_FILTER_NAME, {});
};

const EXPORT_COMPARE_RESULTS_NAME = `${IN_QUEUE_PREFIX}export_compare_results`;
export const segmentTrackExportCompareResults = (
    props: CompareScenariosProperties & { exportName: string }
): void => {
    track(EXPORT_COMPARE_RESULTS_NAME, {
        ...compareScenariosTrackProps(props),
        [EXPORT_NAME_PROPERTY]: props.exportName
    });
};

const SUBMIT_SCENARIO_RUN_NAME = `${IN_QUEUE_PREFIX}submit_scenario_run`;
const SOURCE_PROPERTY = "source";
export type SubmitScenarioRunSource =
    | "scenario_dialog"
    | "run_button"
    | "bulk_rerun"
    | "bulk_upload";
export const segmentTrackSubmitScenarioRun = ({
    scenarioId,
    source
}: {
    scenarioId: string;
    source: SubmitScenarioRunSource;
}): void => {
    track(SUBMIT_SCENARIO_RUN_NAME, {
        [SCENARIO_ID_PROPERTY]: scenarioId,
        [SOURCE_PROPERTY]: source
    });
};

const EXPORT_SCENARIO_DATA_NAME = `${IN_QUEUE_PREFIX}export_scenario_data`;
const EXPORT_NAME_PROPERTY = "export_name";
export const segmentTrackExportScenarioData = ({
    scenarioId,
    exportName
}: {
    scenarioId: string;
    exportName: string;
}): void => {
    track(EXPORT_SCENARIO_DATA_NAME, {
        [SCENARIO_ID_PROPERTY]: scenarioId,
        [EXPORT_NAME_PROPERTY]: exportName
    });
};

const TOGGLE_TABLE_FILTER_NAME = `${IN_QUEUE_PREFIX}toggle_table_filter`;
const TABLE_NAME_PROPERTY = "table_name";
const FILTER_NAME_PROPERTY = "filter_name";
export const segmentTrackToggleInQueueTableFilter = ({
    tableName,
    filterName
}: {
    tableName: string;
    filterName: string;
}): void => {
    track(TOGGLE_TABLE_FILTER_NAME, {
        [TABLE_NAME_PROPERTY]: tableName,
        [FILTER_NAME_PROPERTY]: filterName
    });
};

// Scenario creation workflow
const CHANGE_STARTING_POINT_ASSUMPTIONS = `${IN_QUEUE_PREFIX}change_starting_point_assumptions`;
const STARTING_POINT_NAME = "starting_point_name";
export const segmentTrackChangeStartingPointAssumptions = (
    props: {
        startingPointName: string;
    } & CLUSTER_PROPERTIES
): void => {
    track(CHANGE_STARTING_POINT_ASSUMPTIONS, {
        [STARTING_POINT_NAME]: props.startingPointName,
        [REGION_PROPERTY]: props.region,
        [STUDY_CYCLE_PROPERTY]: props.studyCycle,
        [STUDY_PHASE_PROPERTY]: props.studyPhase,
        [STUDY_GROUP_PROPERTY]: props.studyGroup
    });
};

const UPLOAD_SENSITIVITIES = `${IN_QUEUE_PREFIX}upload_sensitivities`;
export const segmentTrackUploadSensitivities = (
    props: CLUSTER_PROPERTIES
): void => {
    track(UPLOAD_SENSITIVITIES, {
        [REGION_PROPERTY]: props.region,
        [STUDY_CYCLE_PROPERTY]: props.studyCycle,
        [STUDY_PHASE_PROPERTY]: props.studyPhase,
        [STUDY_GROUP_PROPERTY]: props.studyGroup
    });
};

// Speculative projects
const DELETE_SPECULATIVE_PROJECT = `${IN_QUEUE_PREFIX}delete_speculative_project`;
export const segmentTrackDeleteSpeculativeProject = ({
    projectId
}: {
    projectId: string;
}): void => {
    track(DELETE_SPECULATIVE_PROJECT, {
        [PROJECT_ID_PROPERTY]: projectId
    });
};

// Sidebar
const TOGGLE_SIDEBAR_NAME = `${IN_QUEUE_PREFIX}toggle_sidebar`;
const SIDEBAR_STATE_PROPERTY = "sidebar_state";
export const segmentTrackToggleSidebar = ({
    isOpen,
    source
}: {
    isOpen: boolean;
    source?: "menu" | "navbar";
}): void => {
    track(TOGGLE_SIDEBAR_NAME, {
        [SIDEBAR_STATE_PROPERTY]: isOpen ? "open" : "closed",
        ...(source === undefined ? {} : { [SOURCE_PROPERTY]: source })
    });
};

const SELECT_CLUSTER_SIDEBAR_NAME = `${IN_QUEUE_PREFIX}select_cluster_sidebar`;
const CLUSTER_SECTION_PROPERTY = "cluster_section";
export const segmentTrackSelectClusterSidebar = (
    cluster: LegacyClusterInfo,
    clusterSection: "active" | "past"
): void => {
    track(SELECT_CLUSTER_SIDEBAR_NAME, {
        ...clusterProperties(cluster),
        [CLUSTER_SECTION_PROPERTY]: clusterSection
    });
};

// Search palette
const OPEN_SEARCH_PALETTE_NAME = `${IN_QUEUE_PREFIX}open_search_palette`;
export const segmentTrackOpenSearchPalette = (): void => {
    track(OPEN_SEARCH_PALETTE_NAME, {});
};

const SEARCH_PALETTE_ABANDON_NAME = `${IN_QUEUE_PREFIX}search_palette_abandon`;
export const segmentTrackSearchPaletteAbandon = ({
    query
}: {
    query: string;
}): void => {
    track(SEARCH_PALETTE_ABANDON_NAME, {
        [SEARCH_QUERY_PROPERTY]: query
    });
};

const SEARCH_PALETTE_NAVIGATE_NAME = `${IN_QUEUE_PREFIX}search_palette_navigate`;
const SEARCH_QUERY_PROPERTY = "search_query";
const SEARCH_RESULT_CATEGORY_PROPERTY = "search_result_category";
const SEARCH_RESULT_LABEL_PROPERTY = "search_result_label";
export const segmentTrackSearchPaletteNavigate = ({
    query,
    category,
    label
}: {
    query: string;
    category: string;
    label: string;
}): void => {
    track(SEARCH_PALETTE_NAVIGATE_NAME, {
        [SEARCH_QUERY_PROPERTY]: query,
        [SEARCH_RESULT_CATEGORY_PROPERTY]: category,
        [SEARCH_RESULT_LABEL_PROPERTY]: label
    });
};

const CREATE_SPECULATIVE_PROJECTS = `${IN_QUEUE_PREFIX}create_speculative_projects`;
export const segmentTrackCreateSpeculativeProject = (
    props: CLUSTER_PROPERTIES
): void => {
    track(CREATE_SPECULATIVE_PROJECTS, {
        [REGION_PROPERTY]: props.region,
        [STUDY_CYCLE_PROPERTY]: props.studyCycle,
        [STUDY_PHASE_PROPERTY]: props.studyPhase,
        [STUDY_GROUP_PROPERTY]: props.studyGroup
    });
};

const CLICK_DOCUMENTATION_NAME = `${IN_QUEUE_PREFIX}click_documentation`;
const PAGE_PATH_PROPERTY = "page_path";
export type ClickDocumentationSource = "nav" | "cluster_banner";
export const segmentTrackClickDocumentation = ({
    pagePath,
    source
}: {
    pagePath: string;
    source: ClickDocumentationSource;
}): void => {
    track(CLICK_DOCUMENTATION_NAME, {
        [PAGE_PATH_PROPERTY]: pagePath,
        [SOURCE_PROPERTY]: source
    });
};

// Search button (magnifying glass). Complements open_search_palette (which
// fires for every open, including the keyboard shortcut) by isolating the
// toolbar icon specifically.
const CLICK_SEARCH_BUTTON_NAME = `${IN_QUEUE_PREFIX}click_search_button`;
export const segmentTrackClickSearchButton = ({
    pagePath
}: {
    pagePath: string;
}): void => {
    track(CLICK_SEARCH_BUTTON_NAME, {
        [PAGE_PATH_PROPERTY]: pagePath
    });
};

// Clicking a cluster the team is not subscribed to (upsell demand signal).
const CLICK_UNAVAILABLE_CLUSTER_NAME = `${IN_QUEUE_PREFIX}click_unavailable_cluster`;
export const segmentTrackClickUnavailableCluster = (
    cluster: LegacyClusterInfo
): void => {
    track(CLICK_UNAVAILABLE_CLUSTER_NAME, clusterProperties(cluster));
};

// Project rename (pencil button, admin-only)
const RENAME_PROJECT_NAME = `${IN_QUEUE_PREFIX}rename_project`;
const IS_SPECULATIVE_PROPERTY = "is_speculative";
const HAD_PREVIOUS_NAME_PROPERTY = "had_previous_name";
export const segmentTrackRenameProject = ({
    projectId,
    isSpeculative,
    hadPreviousName
}: {
    projectId: string;
    isSpeculative: boolean;
    hadPreviousName: boolean;
}): void => {
    track(RENAME_PROJECT_NAME, {
        [PROJECT_ID_PROPERTY]: projectId,
        [IS_SPECULATIVE_PROPERTY]: isSpeculative,
        [HAD_PREVIOUS_NAME_PROPERTY]: hadPreviousName
    });
};

// Exporting the project-page scenarios list (previously untracked)
const EXPORT_PROJECT_SCENARIOS_NAME = `${IN_QUEUE_PREFIX}export_project_scenarios`;
const NUM_SCENARIOS_PROPERTY = "num_scenarios";
export const segmentTrackExportProjectScenarios = ({
    projectId,
    numScenarios
}: {
    projectId: string;
    numScenarios: number;
}): void => {
    track(EXPORT_PROJECT_SCENARIOS_NAME, {
        [PROJECT_ID_PROPERTY]: projectId,
        [NUM_SCENARIOS_PROPERTY]: numScenarios
    });
};

// Setting scenario sensitivities — unified across CSV and UI entry so the two
// can be compared directly (group by `method` in Amplitude).
export type SetSensitivitiesMethod =
    | "csv"
    | "ui_project_card"
    | "ui_project_table";
const SET_SENSITIVITIES_NAME = `${IN_QUEUE_PREFIX}set_sensitivities`;
const METHOD_PROPERTY = "method";
const NUM_PROJECTS_AFFECTED_PROPERTY = "num_projects_affected";
const SUCCESS_PROPERTY = "success";
const NUM_INVALID_PROJECTS_PROPERTY = "num_invalid_projects";
export const segmentTrackSetSensitivities = ({
    method,
    numProjectsAffected,
    cluster,
    success,
    numInvalidProjects
}: {
    method: SetSensitivitiesMethod;
    numProjectsAffected: number;
    cluster: LegacyClusterInfo;
    success?: boolean;
    numInvalidProjects?: number;
}): void => {
    track(SET_SENSITIVITIES_NAME, {
        [METHOD_PROPERTY]: method,
        [NUM_PROJECTS_AFFECTED_PROPERTY]: numProjectsAffected,
        ...clusterProperties(cluster),
        ...(success !== undefined ? { [SUCCESS_PROPERTY]: success } : {}),
        ...(numInvalidProjects !== undefined
            ? { [NUM_INVALID_PROJECTS_PROPERTY]: numInvalidProjects }
            : {})
    });
};

const DOWNLOAD_SENSITIVITIES_TEMPLATE_NAME = `${IN_QUEUE_PREFIX}download_sensitivities_template`;
export const segmentTrackDownloadSensitivitiesTemplate = (
    cluster: LegacyClusterInfo
): void => {
    track(DOWNLOAD_SENSITIVITIES_TEMPLATE_NAME, clusterProperties(cluster));
};

// Scenario results table — column-header filtering
const SCENARIO_TABLE_COLUMN_FILTERED_NAME = `${IN_QUEUE_PREFIX}scenario_table_column_filtered`;
const FILTERED_COLUMNS_PROPERTY = "filtered_columns";
const NUM_ACTIVE_FILTERS_PROPERTY = "num_active_filters";
export const segmentTrackScenarioTableColumnFiltered = ({
    tableName,
    filteredColumns,
    numActiveFilters
}: {
    tableName: string;
    filteredColumns: string;
    numActiveFilters: number;
}): void => {
    track(SCENARIO_TABLE_COLUMN_FILTERED_NAME, {
        [TABLE_NAME_PROPERTY]: tableName,
        [FILTERED_COLUMNS_PROPERTY]: filteredColumns,
        [NUM_ACTIVE_FILTERS_PROPERTY]: numActiveFilters
    });
};

// Any IQ table — column resize (drag handle only)
const TABLE_COLUMN_RESIZED_NAME = `${IN_QUEUE_PREFIX}table_column_resized`;
const COLUMN_ID_PROPERTY = "column_id";
const NEW_WIDTH_PROPERTY = "new_width";
export const segmentTrackTableColumnResized = ({
    tableName,
    columnId,
    newWidth
}: {
    tableName: string;
    columnId: string;
    newWidth: number;
}): void => {
    track(TABLE_COLUMN_RESIZED_NAME, {
        [TABLE_NAME_PROPERTY]: tableName,
        [COLUMN_ID_PROPERTY]: columnId,
        [NEW_WIDTH_PROPERTY]: newWidth
    });
};

// Project page charts: the metric both charts plot on their Y axis
const CHANGE_CHART_METRIC_NAME = `${IN_QUEUE_PREFIX}change_chart_metric`;
const CHART_METRIC_PROPERTY = "chart_metric";
export const segmentTrackChangeChartMetric = ({
    metric
}: {
    metric: string;
}): void => {
    track(CHANGE_CHART_METRIC_NAME, {
        [CHART_METRIC_PROPERTY]: metric
    });
};

const CHANGE_DROPOFF_CHART_X_AXIS_NAME = `${IN_QUEUE_PREFIX}change_dropoff_chart_x_axis`;
const X_AXIS_PROPERTY = "x_axis";
export const segmentTrackChangeDropoffChartXAxis = ({
    xAxis
}: {
    xAxis: string;
}): void => {
    track(CHANGE_DROPOFF_CHART_X_AXIS_NAME, {
        [X_AXIS_PROPERTY]: xAxis
    });
};

// Project-ranking chart (scenario page): cohort toggle (Related vs All)
const CHANGE_RANKING_CHART_COHORT_NAME = `${IN_QUEUE_PREFIX}change_ranking_chart_cohort`;
const COHORT_PROPERTY = "cohort";
const IS_LIMITED_SUB_PROPERTY = "is_limited_sub";
export const segmentTrackChangeRankingChartCohort = ({
    cohort,
    isLimitedSub
}: {
    cohort: string;
    isLimitedSub: boolean;
}): void => {
    track(CHANGE_RANKING_CHART_COHORT_NAME, {
        [COHORT_PROPERTY]: cohort,
        [IS_LIMITED_SUB_PROPERTY]: isLimitedSub
    });
};

// Project-ranking chart (scenario page): metric toggle ($/MW vs total $)
const CHANGE_RANKING_CHART_METRIC_NAME = `${IN_QUEUE_PREFIX}change_ranking_chart_metric`;
const METRIC_PROPERTY = "metric";
export const segmentTrackChangeRankingChartMetric = ({
    metric
}: {
    metric: string;
}): void => {
    track(CHANGE_RANKING_CHART_METRIC_NAME, {
        [METRIC_PROPERTY]: metric
    });
};

// Project page: "Latest models only" vs "All scenarios" toggle
const TOGGLE_LATEST_MODELS_ONLY_NAME = `${IN_QUEUE_PREFIX}toggle_latest_models_only`;
const LATEST_MODELS_ENABLED_PROPERTY = "enabled";
export const segmentTrackToggleLatestModelsOnly = ({
    projectId,
    enabled
}: {
    projectId: string;
    enabled: boolean;
}): void => {
    track(TOGGLE_LATEST_MODELS_ONLY_NAME, {
        [PROJECT_ID_PROPERTY]: projectId,
        [LATEST_MODELS_ENABLED_PROPERTY]: enabled
    });
};

// Sensitivity chart (project page): click a bar to navigate to that scenario
const CLICK_SCENARIO_FROM_SENSITIVITY_CHART_NAME = `${IN_QUEUE_PREFIX}click_scenario_from_sensitivity_chart`;
const POSITION_PROPERTY = "position";
export const segmentTrackClickScenarioFromSensitivityChart = ({
    scenarioId,
    position
}: {
    scenarioId: string;
    position: number;
}): void => {
    track(CLICK_SCENARIO_FROM_SENSITIVITY_CHART_NAME, {
        [SCENARIO_ID_PROPERTY]: scenarioId,
        [POSITION_PROPERTY]: position
    });
};

// Top Upgrades chart (scenario page): click an upgrade bar to expand/collapse
// the inline breakdown table.
const CLICK_UPGRADE_IN_TOP_UPGRADES_CHART_NAME = `${IN_QUEUE_PREFIX}click_upgrade_in_top_upgrades_chart`;
const UPGRADE_ID_PROPERTY = "upgrade_id";
const EXPANDED_PROPERTY = "expanded";
export const segmentTrackClickUpgradeInTopUpgradesChart = ({
    upgradeId,
    expanded
}: {
    upgradeId: string;
    expanded: boolean;
}): void => {
    track(CLICK_UPGRADE_IN_TOP_UPGRADES_CHART_NAME, {
        [UPGRADE_ID_PROPERTY]: upgradeId,
        [EXPANDED_PROPERTY]: expanded
    });
};

const FAVORITE_PROJECT_NAME = `${IN_QUEUE_PREFIX}favorite_project`;
const IS_FAVORITED_PROPERTY = "is_favorited";
export const segmentTrackFavoriteProject = ({
    projectId,
    isFavorited
}: {
    projectId: string;
    isFavorited: boolean;
}): void => {
    track(FAVORITE_PROJECT_NAME, {
        [PROJECT_ID_PROPERTY]: projectId,
        [IS_FAVORITED_PROPERTY]: isFavorited
    });
};

const NUM_FILES_PROPERTY = "num_files";
const CREATE_CUSTOM_UPGRADE_NAME = `${IN_QUEUE_PREFIX}create_custom_upgrade`;
export const segmentTrackCreateCustomUpgrade = ({
    cluster,
    numFiles,
    upgradeId
}: {
    cluster: LegacyClusterInfo;
    numFiles: number;
    upgradeId?: string;
}): void => {
    track(CREATE_CUSTOM_UPGRADE_NAME, {
        ...clusterProperties(cluster),
        [NUM_FILES_PROPERTY]: numFiles,
        ...(upgradeId !== undefined ? { [UPGRADE_ID_PROPERTY]: upgradeId } : {})
    });
};

const ARCHIVE_CUSTOM_UPGRADE_NAME = `${IN_QUEUE_PREFIX}archive_custom_upgrade`;
export const segmentTrackArchiveCustomUpgrade = ({
    cluster,
    upgradeId
}: {
    cluster: LegacyClusterInfo;
    upgradeId: string;
}): void => {
    track(ARCHIVE_CUSTOM_UPGRADE_NAME, {
        ...clusterProperties(cluster),
        [UPGRADE_ID_PROPERTY]: upgradeId
    });
};

export type RequestCustomUpgradeValidationSource = "create" | "revalidate";
const REQUEST_CUSTOM_UPGRADE_VALIDATION_NAME = `${IN_QUEUE_PREFIX}request_custom_upgrade_validation`;
export const segmentTrackRequestCustomUpgradeValidation = ({
    cluster,
    upgradeId,
    source
}: {
    cluster: LegacyClusterInfo;
    upgradeId: string;
    source: RequestCustomUpgradeValidationSource;
}): void => {
    track(REQUEST_CUSTOM_UPGRADE_VALIDATION_NAME, {
        ...clusterProperties(cluster),
        [UPGRADE_ID_PROPERTY]: upgradeId,
        [SOURCE_PROPERTY]: source
    });
};

// The team has not bought the add-on and clicked the disabled entry point
// (upsell demand signal, like click_unavailable_cluster).
const CLICK_CUSTOM_UPGRADES_LOCKED_NAME = `${IN_QUEUE_PREFIX}click_custom_upgrades_locked`;
export const segmentTrackClickCustomUpgradesLocked = (
    cluster: LegacyClusterInfo
): void => {
    track(CLICK_CUSTOM_UPGRADES_LOCKED_NAME, clusterProperties(cluster));
};

// Scenario builder bulk bar: Exclude or Downsize applied to the selection.
const APPLY_BULK_PROJECT_ACTION_NAME = `${IN_QUEUE_PREFIX}apply_bulk_project_action`;
const ACTION_PROPERTY = "action";
const NUM_PROJECTS_PROPERTY = "num_projects";
const ERIS_REDUCTION_PROPERTY = "eris_reduction";
const NRIS_REDUCTION_PROPERTY = "nris_reduction";
export const segmentTrackApplyBulkProjectAction = ({
    cluster,
    action,
    numProjects,
    erisReduction,
    nrisReduction
}: {
    cluster: LegacyClusterInfo;
    action: string;
    numProjects: number;
    erisReduction?: number;
    nrisReduction?: number;
}): void => {
    track(APPLY_BULK_PROJECT_ACTION_NAME, {
        ...clusterProperties(cluster),
        [ACTION_PROPERTY]: action,
        [NUM_PROJECTS_PROPERTY]: numProjects,
        ...(erisReduction !== undefined
            ? { [ERIS_REDUCTION_PROPERTY]: erisReduction }
            : {}),
        ...(nrisReduction !== undefined
            ? { [NRIS_REDUCTION_PROPERTY]: nrisReduction }
            : {})
    });
};

const DISMISS_ERIS_BELOW_NRIS_WARNING_NAME = `${IN_QUEUE_PREFIX}dismiss_eris_below_nris_warning`;
export const segmentTrackDismissErisBelowNrisWarning = ({
    cluster,
    numProjects
}: {
    cluster: LegacyClusterInfo;
    numProjects: number;
}): void => {
    track(DISMISS_ERIS_BELOW_NRIS_WARNING_NAME, {
        ...clusterProperties(cluster),
        [NUM_PROJECTS_PROPERTY]: numProjects
    });
};

export type ProjectFilterTableName =
    | "cluster_projects"
    | "scenario_builder_projects";
const CHANGE_PROJECT_FILTER_NAME = `${IN_QUEUE_PREFIX}change_project_filter`;
const FILTER_VALUE_PROPERTY = "value";
export const segmentTrackChangeProjectFilter = ({
    tableName,
    filterName,
    value
}: {
    tableName: ProjectFilterTableName;
    filterName: string;
    value: string;
}): void => {
    track(CHANGE_PROJECT_FILTER_NAME, {
        [TABLE_NAME_PROPERTY]: tableName,
        [FILTER_NAME_PROPERTY]: filterName,
        [FILTER_VALUE_PROPERTY]: value
    });
};

const EXPORT_CLUSTER_PROJECTS_NAME = `${IN_QUEUE_PREFIX}export_cluster_projects`;
const NUM_ROWS_PROPERTY = "num_rows";
export const segmentTrackExportClusterProjects = ({
    cluster,
    numRows
}: {
    cluster: LegacyClusterInfo;
    numRows: number;
}): void => {
    track(EXPORT_CLUSTER_PROJECTS_NAME, {
        ...clusterProperties(cluster),
        [NUM_ROWS_PROPERTY]: numRows
    });
};

// Bulk scenario upload: each uploaded sensitivities CSV becomes one scenario.
const OPEN_BULK_SCENARIO_UPLOAD_NAME = `${IN_QUEUE_PREFIX}open_bulk_scenario_upload`;
export const segmentTrackOpenBulkScenarioUpload = ({
    cluster,
    projectId,
    scenarioStage
}: {
    cluster: LegacyClusterInfo;
    projectId: string;
    scenarioStage: string;
}): void => {
    track(OPEN_BULK_SCENARIO_UPLOAD_NAME, {
        ...clusterProperties(cluster),
        [PROJECT_ID_PROPERTY]: projectId,
        [SCENARIO_STAGE_PROPERTY]: scenarioStage
    });
};

const UPLOAD_BULK_SCENARIO_FILES_NAME = `${IN_QUEUE_PREFIX}upload_bulk_scenario_files`;
const NUM_REJECTED_FILES_PROPERTY = "num_rejected_files";
export const segmentTrackUploadBulkScenarioFiles = ({
    cluster,
    projectId,
    numFiles,
    numRejectedFiles
}: {
    cluster: LegacyClusterInfo;
    projectId: string;
    numFiles: number;
    numRejectedFiles: number;
}): void => {
    track(UPLOAD_BULK_SCENARIO_FILES_NAME, {
        ...clusterProperties(cluster),
        [PROJECT_ID_PROPERTY]: projectId,
        [NUM_FILES_PROPERTY]: numFiles,
        [NUM_REJECTED_FILES_PROPERTY]: numRejectedFiles
    });
};

const VIEW_BULK_SCENARIO_ASSUMPTIONS_NAME = `${IN_QUEUE_PREFIX}view_bulk_scenario_assumptions`;
export const segmentTrackViewBulkScenarioAssumptions = ({
    projectId
}: {
    projectId: string;
}): void => {
    track(VIEW_BULK_SCENARIO_ASSUMPTIONS_NAME, {
        [PROJECT_ID_PROPERTY]: projectId
    });
};

type BulkScenarioSetting =
    | "cost_assumptions"
    | "custom_upgrade"
    | "run_on_submit";
const APPLY_BULK_SCENARIO_SETTING_NAME = `${IN_QUEUE_PREFIX}apply_bulk_scenario_setting`;
const SETTING_PROPERTY = "setting";
export type BulkScenarioSettingSource = "selection_bar" | "row";
export const segmentTrackApplyBulkScenarioSetting = ({
    projectId,
    setting,
    value,
    numScenarios,
    source
}: {
    projectId: string;
    setting: BulkScenarioSetting;
    // The cost assumption type, the custom upgrade id ("" for none) or the run flag.
    value: string | boolean;
    numScenarios: number;
    source: BulkScenarioSettingSource;
}): void => {
    track(APPLY_BULK_SCENARIO_SETTING_NAME, {
        [PROJECT_ID_PROPERTY]: projectId,
        [SETTING_PROPERTY]: setting,
        [FILTER_VALUE_PROPERTY]: value,
        [NUM_SCENARIOS_PROPERTY]: numScenarios,
        [SOURCE_PROPERTY]: source
    });
};

const CREATE_BULK_SCENARIOS_NAME = `${IN_QUEUE_PREFIX}create_bulk_scenarios`;
const NUM_RUN_ON_SUBMIT_PROPERTY = "num_run_on_submit";
export const segmentTrackCreateBulkScenarios = ({
    cluster,
    projectId,
    scenarioStage,
    numScenarios,
    numRunOnSubmit
}: {
    cluster: LegacyClusterInfo;
    projectId: string;
    scenarioStage: string;
    numScenarios: number;
    numRunOnSubmit: number;
}): void => {
    track(CREATE_BULK_SCENARIOS_NAME, {
        ...clusterProperties(cluster),
        [PROJECT_ID_PROPERTY]: projectId,
        [SCENARIO_STAGE_PROPERTY]: scenarioStage,
        [NUM_SCENARIOS_PROPERTY]: numScenarios,
        [NUM_RUN_ON_SUBMIT_PROPERTY]: numRunOnSubmit
    });
};

type BulkScenarioFileIssue = "warnings" | "rejected";
const VIEW_BULK_SCENARIO_FILE_ISSUES_NAME = `${IN_QUEUE_PREFIX}view_bulk_scenario_file_issues`;
const ISSUE_PROPERTY = "issue";
export const segmentTrackViewBulkScenarioFileIssues = ({
    projectId,
    issue
}: {
    projectId: string;
    issue: BulkScenarioFileIssue;
}): void => {
    track(VIEW_BULK_SCENARIO_FILE_ISSUES_NAME, {
        [PROJECT_ID_PROPERTY]: projectId,
        [ISSUE_PROPERTY]: issue
    });
};

const REMOVE_BULK_SCENARIO_NAME = `${IN_QUEUE_PREFIX}remove_bulk_scenario`;
export const segmentTrackRemoveBulkScenario = ({
    projectId
}: {
    projectId: string;
}): void => {
    track(REMOVE_BULK_SCENARIO_NAME, {
        [PROJECT_ID_PROPERTY]: projectId
    });
};

export type BulkScenarioTitleFormat = "humanize" | "prefix" | "suffix";
const FORMAT_BULK_SCENARIO_TITLES_NAME = `${IN_QUEUE_PREFIX}format_bulk_scenario_titles`;
const FORMAT_PROPERTY = "format";
export const segmentTrackFormatBulkScenarioTitles = ({
    projectId,
    numScenarios,
    format
}: {
    projectId: string;
    numScenarios: number;
    format: BulkScenarioTitleFormat;
}): void => {
    track(FORMAT_BULK_SCENARIO_TITLES_NAME, {
        [PROJECT_ID_PROPERTY]: projectId,
        [NUM_SCENARIOS_PROPERTY]: numScenarios,
        [FORMAT_PROPERTY]: format
    });
};

const OPEN_BULK_SCENARIO_CREATE_NAME = `${IN_QUEUE_PREFIX}open_bulk_scenario_create`;
export const segmentTrackOpenBulkScenarioCreate = ({
    projectId,
    numScenarios
}: {
    projectId: string;
    numScenarios: number;
}): void => {
    track(OPEN_BULK_SCENARIO_CREATE_NAME, {
        [PROJECT_ID_PROPERTY]: projectId,
        [NUM_SCENARIOS_PROPERTY]: numScenarios
    });
};
